import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import MomentHelpers from "../helpers/MomentHelpers";
import { CompletedItemObject } from "../models/CompletedItemObject";
import {
  ThemeColor,
  // Pos,
  MenuObject,
  CallServer,
  PayPageSessionResponse,
  TipSelections,
} from "../models/interfaces";
import { ItemObject } from "../models/ItemObject";
import { OrderedItem } from "../socketType";
import { RootState } from "../utils/store";
import { isTruthy } from "../helpers/methods";

export interface MenuState {
  restaurantId: string;
  categorySelected: string;
  sessionId: string;
  tabSession: string;
  item: ItemObject;
  addedItems: CompletedItemObject[];
  theme: ThemeColor;
  edit: boolean;
  restaurantName: string;
  logo: string;
  tableNumber: string;
  tip: number;
  tax: number;
  credit: number;
  charges: {
    [key: string]: {
      displayName: string;
      value: number;
    };
  };
  fee: number;
  subtotal: number;
  loading: boolean;
  total: number;
  session: PayPageSessionResponse;
  showTabModal: boolean;
  isOpen: boolean;
  promoCode: string;
  promoName: string;
  promoDiscount: number;
  customTipPromo: boolean;
  tipPercentage: number;
  tabOrdersVisible: boolean;
  selectedMenu: MenuObject;
  menuList: MenuObject[];
  callServer: CallServer;
  tabsEnabled: boolean;
  isTableVerified: boolean;
  restaurantBanner: string;
  tabNewItemCount: number | null;
  addedItemsForEdit: any;
  customTip: number;
  splittable: boolean;
  pos: string;
  canPlaceOrder: boolean;
  paymentMethodRequired: boolean;
  tipSelection: TipSelections;
  splitCheck: boolean;
  notificationEmailRequired: boolean;
  idemp: {
    mode: string;
    key: string;
    hash: string;
  };
  restaurantConfig: {
    payment_page_disclaimer?: string;
  };
  loyalty?: {
    provider?: string;
    isLoyaltyEnabled?: boolean;
  };
}

const initialState: MenuState = {
  restaurantId: "",
  categorySelected: "",
  sessionId: "",
  tabSession: "",
  item: {} as ItemObject,
  addedItems: [],
  theme: {} as ThemeColor,
  edit: false,
  restaurantName: "",
  logo: "",
  tableNumber: "",
  loading: false,
  tip: 0,
  fee: 0,
  tax: 0,
  credit: 0,
  charges: {},
  subtotal: 0,
  total: 0,
  session: {
    sessionItems: {},
    waiterAdded: [] as OrderedItem[],
    splitPayState: {},
  } as PayPageSessionResponse,
  showTabModal: false,
  isOpen: true,
  promoDiscount: 0,
  promoCode: "",
  customTipPromo: false,
  tipPercentage: 22,
  promoName: "",
  tabOrdersVisible: false,
  pos: "",
  selectedMenu: {
    id: "",
    name: "",
    colors: {},
  } as MenuObject,
  menuList: [] as MenuObject[],
  callServer: {
    presets: [],
    visible: false,
  },
  tabsEnabled: false,
  isTableVerified: false,
  restaurantBanner: "",
  tabNewItemCount: null,
  addedItemsForEdit: {
    expandedMods: {},
    selectedMods: {},
    selectedPriceLevel: {},
  },
  customTip: 0,
  splittable: true,
  canPlaceOrder: false,
  paymentMethodRequired: false,
  tipSelection: {
    default: 2,
    tips: [18, 20, 22],
  },
  splitCheck: false,
  notificationEmailRequired: false,
  idemp: {
    mode: "pay",
    key: "",
    hash: "",
  },
  restaurantConfig: {
    payment_page_disclaimer: "",
  },
  loyalty: undefined,
};

export const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    addItems: (state, action: PayloadAction<CompletedItemObject[]>) => {
      state.addedItems = action.payload;
    },
    addRestaurantName: (state, action: PayloadAction<string>) => {
      state.restaurantName = action.payload;
    },
    addSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    addCategorySelected: (state, action: PayloadAction<string>) => {
      state.categorySelected = action.payload;
    },
    addTabSession: (state, action: PayloadAction<string>) => {
      state.tabSession = action.payload;
    },
    addRestaurantId: (state, action: PayloadAction<string>) => {
      state.restaurantId = action.payload;
    },
    addItem: (state, action: PayloadAction<ItemObject>) => {
      state.item = action.payload;
    },
    toggleEdit: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    addTableNumber: (state, action: PayloadAction<string>) => {
      state.tableNumber = action.payload;
    },
    addSession: (state, action: PayloadAction<PayPageSessionResponse>) => {
      state.session = action.payload;
    },
    addTip: (state, action: PayloadAction<number>) => {
      state.tip = action.payload;
    },
    addSubtotal: (state, action: PayloadAction<number>) => {
      state.subtotal = action.payload;
    },
    addTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },
    addTax: (state, action: PayloadAction<number>) => {
      state.tax = action.payload;
    },
    addCharges: (
      state,
      action: PayloadAction<{
        [key: string]: {
          displayName: string;
          value: number;
        };
      }>
    ) => {
      state.charges = action.payload;
    },
    addLogo: (state, action: PayloadAction<string>) => {
      state.logo = action.payload;
    },
    addRestaurantBanner: (state, action: PayloadAction<string>) => {
      state.restaurantBanner = action.payload;
    },
    addFee: (state, action: PayloadAction<number>) => {
      state.fee = action.payload;
    },
    addTabSessionId: (state, action: PayloadAction<string>) => {
      state.tabSession = action.payload;
    },
    addPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
    addPromoDiscount: (state, action: PayloadAction<number>) => {
      state.promoDiscount = action.payload;
    },
    addTipPercentage: (state, action: PayloadAction<number>) => {
      state.tipPercentage = action.payload;
    },
    addPromoName: (state, action: PayloadAction<string>) => {
      state.promoName = action.payload;
    },
    addCustomTipPromo: (state, action: PayloadAction<boolean>) => {
      state.customTipPromo = action.payload;
    },
    updateEditItem: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    addTabOrdersVisible: (state, action: PayloadAction<boolean>) => {
      state.tabOrdersVisible = action.payload;
    },
    addTheme: (state, action: PayloadAction<ThemeColor>) => {
      state.theme = { ...action.payload };
    },
    addPos: (state, action: PayloadAction<string>) => {
      state.pos = action.payload;
    },
    addMenus: (state, action: PayloadAction<MenuObject[]>) => {
      state.menuList = action.payload;
    },
    addSelectedMenu: (state, action: PayloadAction<MenuObject>) => {
      state.selectedMenu = action.payload;
    },
    addPaymentProperties: (
      state,
      action: PayloadAction<{
        total?: number;
        subtotal?: number;
        fee?: number;
        tax?: number;
        tip?: number;
        credit?: number;
        charges?: {
          [key: string]: {
            displayName: string;
            value: number;
          };
        };
      }>
    ) => {
      state.total =
        action.payload.total !== undefined ? action.payload.total : state.total;
      state.subtotal =
        action.payload.subtotal !== undefined
          ? action.payload.subtotal
          : state.subtotal;
      state.fee =
        action.payload.fee !== undefined ? action.payload.fee : state.fee;
      state.tax =
        action.payload.tax !== undefined ? action.payload.tax : state.tax;
      state.tip =
        action.payload.tip !== undefined ? action.payload.tip : state.tip;
      state.charges = action.payload.charges ?? state.charges;
      state.credit = action.payload.credit ?? state.credit;
    },
    closeSession: (state) => {
      state.sessionId = "";
      state.tabSession = "";
    },
    closeSessionPayment: (state) => {
      state.addedItems = [];
      state.tip = 0;
      state.total = 0;
      state.credit = 0;
      state.item = {} as ItemObject;
      MomentHelpers.getCurrentDate();
      state.subtotal = 0;
      state.fee = 0;
      state.promoCode = "";
      state.promoName = "";
      state.promoDiscount = 0;
      state.tax = 0;
      state.session = {
        sessionItems: {},
        waiterAdded: [] as OrderedItem[],
        splitPayState: {},
      } as PayPageSessionResponse;
      state.tipPercentage = isTruthy(state.tipSelection)
        ? state.tipSelection.tips[state.tipSelection.default]
        : 22;
      state.customTipPromo = false;
    },
    isTabsEnabled: (state, action: PayloadAction<boolean>) => {
      state.tabsEnabled = action.payload;
    },
    updateIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    isTableNumVerified: (state, action: PayloadAction<boolean>) => {
      state.isTableVerified = action.payload;
    },
    updateCallServer: (state, action: PayloadAction<CallServer>) => {
      state.callServer = action.payload;
    },
    addTabNewItemCount: (state, action: PayloadAction<number | null>) => {
      state.tabNewItemCount = action.payload;
    },
    updateAddedItemsForEdit: (state, action: PayloadAction<any>) => {
      state.addedItemsForEdit = action.payload;
    },
    updateCustomTipValue: (state, action: PayloadAction<number>) => {
      state.customTip = action.payload;
    },
    updateSplittableState: (state, action: PayloadAction<boolean>) => {
      state.splittable = action.payload;
    },
    updateCanPlaceOrder: (state, action: PayloadAction<boolean>) => {
      state.canPlaceOrder = action.payload;
    },
    updatePaymentMethodRequired: (state, action: PayloadAction<boolean>) => {
      state.paymentMethodRequired = action.payload;
    },
    addTipSelection: (state, action: PayloadAction<TipSelections>) => {
      state.tipSelection = action.payload;
    },
    updateSplitCheckState: (state, action: PayloadAction<boolean>) => {
      state.splitCheck = action.payload;
    },
    addNotificationEmailRequired: (state, action: PayloadAction<boolean>) => {
      state.notificationEmailRequired = action.payload;
    },
    addIdemp: (
      state,
      action: PayloadAction<{ mode: string; key: string; hash: string }>
    ) => {
      state.idemp = action.payload;
    },
    addRestaurantConfig: (
      state,
      action: PayloadAction<{ payment_page_disclaimer?: string }>
    ) => {
      state.restaurantConfig = action.payload;
    },
    addLoyalty: (
      state,
      action: PayloadAction<{ provider?: string; isLoyaltyEnabled?: boolean } | undefined>
    ) => {
      state.loyalty = action.payload;
    },
  },
});

export const {
  addLogo,
  addRestaurantBanner,
  addItems,
  addPromoCode,
  addPromoDiscount,
  addSubtotal,
  addSessionId,
  addTabSession,
  addSession,
  addTip,
  addCustomTipPromo,
  addFee,
  addCharges,
  addPaymentProperties,
  addTax,
  addTotal,
  addTabSessionId,
  addPromoName,
  addTipPercentage,
  addTheme,
  closeSession,
  closeSessionPayment,
  addRestaurantId,
  addItem,
  updateEditItem,
  addRestaurantName,
  toggleEdit,
  addTabOrdersVisible,
  addTableNumber,
  addCategorySelected,
  addPos,
  addSelectedMenu,
  addMenus,
  updateCallServer,
  isTabsEnabled,
  isTableNumVerified,
  updateIsOpen,
  addTabNewItemCount,
  updateAddedItemsForEdit,
  updateCustomTipValue,
  updateSplittableState,
  updateCanPlaceOrder,
  updatePaymentMethodRequired,
  addTipSelection,
  updateSplitCheckState,
  addNotificationEmailRequired,
  addIdemp,
  addRestaurantConfig,
  addLoyalty,
} = menuSlice.actions;

export const selectAddedItems = (state: RootState) => state.menu.addedItems;
export const selectEditItem = (state: RootState) => state.menu.edit;
export const selectTheme = (state: RootState) => state.menu.theme;
export const selectSwitchToPay = (state: RootState) =>
  state.menu.tabOrdersVisible;
export const selectTipPercentage = (state: RootState) =>
  state.menu.tipPercentage;
export const selectSessionId = (state: RootState) => state.menu.sessionId;
export const selectPromoName = (state: RootState) => state.menu.promoName;
export const selectSubtotal = (state: RootState) => state.menu.subtotal;
export const selectFee = (state: RootState) => state.menu.fee;
export const selectCustomTipPromo = (state: RootState) =>
  state.menu.customTipPromo;
export const selectTabSessionId = (state: RootState) => state.menu.tabSession;
export const selectPromoCode = (state: RootState) => state.menu.promoCode;
export const selectTax = (state: RootState) => state.menu.tax;
export const selectPromoDiscount = (state: RootState) =>
  state.menu.promoDiscount;
export const selectSession = (state: RootState) => state.menu.session;
export const selectTip = (state: RootState) => state.menu.tip;
export const selectTableNumber = (state: RootState) => state.menu.tableNumber;
export const selectTotal = (state: RootState) => state.menu.total;
export const selectRestaurantId = (state: RootState) => state.menu.restaurantId;
export const selectCategorySelected = (state: RootState) =>
  state.menu.categorySelected;
export const selectItem = (state: RootState) => state.menu.item;
export const selectEdit = (state: RootState) => state.menu.edit;
export const selectRestaurantName = (state: RootState) =>
  state.menu.restaurantName;
export const selectPos = (state: RootState) => state.menu.pos;
export const selectSelectedMenu = (state: RootState) => state.menu.selectedMenu;
export const selectMenuList = (state: RootState) => state.menu.menuList;
export const selectLogo = (state: RootState) =>
  state.menu.logo ? state.menu.logo : undefined;
export const selectAddedItemsThankYouPage = (state: RootState) =>
  state.menu.addedItems ? state.menu.addedItems : undefined;
export const selectCallServer = (state: RootState) =>
  state.menu.callServer ? state.menu.callServer : undefined;
export const selectIsTabsEnabled = (state: RootState) => state.menu.tabsEnabled;
export const selectIsOpen = (state: RootState) => state.menu.isOpen;
export const selectRestaurantBanner = (state: RootState) =>
  state.menu.restaurantBanner;
export const selectIsTableNumVerified = (state: RootState) =>
  state.menu.isTableVerified;
export const selectTabNewItemCount = (state: RootState) =>
  state.menu.tabNewItemCount;
export const selectCharges = (state: RootState) => state.menu.charges;
export const selectCredit = (state: RootState) => state.menu.credit;
export const selectCustomTipValue = (state: RootState) => state.menu.customTip;
export const selectAddedItemsForEdit = (state: RootState) =>
  state.menu.addedItemsForEdit;
export const selectSplittableState = (state: RootState) =>
  state.menu.splittable;
export const selectCanPlaceOrder = (state: RootState) =>
  state.menu.canPlaceOrder;
export const selectPaymentMethodRequired = (state: RootState) =>
  state.menu.paymentMethodRequired;
export const selectTipSelection = (state: RootState) => state.menu.tipSelection;
export const selectSplitCheckState = (state: RootState) =>
  state.menu.splitCheck;
export const selectNotificationEmailRequired = (state: RootState) =>
  state.menu.notificationEmailRequired;
export const selectIdemp = (state: RootState) => state.menu.idemp;
export const selectRestaurantConfig = (state: RootState) =>
  state.menu.restaurantConfig;
export const selectPaymentPageDisclaimer = (state: RootState) =>
  state.menu.restaurantConfig?.payment_page_disclaimer;
export const selectLoyalty = (state: RootState) => state.menu.loyalty;

export default menuSlice.reducer;
